import React from "react";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { skipLinkL } from "@/i18n/common";

/**
 * "Skip to content". App mounts it once, ahead of the cookie banner and the
 * header, so it is the first Tab stop on every public and partner page.
 * Hidden until it is focused, then a white tab in the top corner (the inline
 * start, so the right in Arabic) with the focus ring (--tk-focus, from
 * :focus-visible in index.css).
 *
 * It moves focus to the main landmark (which carries tabIndex=-1) without
 * putting #main in the address bar, and without the browser's own focus
 * scroll: the content already starts under the sticky header, and that scroll
 * pushed its first line beneath it. From further down the page it returns to
 * the top, where the content starts.
 */
export default function SkipLink({ target = "main" }) {
  const { lang } = useSettings();
  const l = pick(skipLinkL, lang);
  const skip = (event) => {
    event.preventDefault();
    const node = document.getElementById(target);
    if (!node) return;
    node.focus({ preventScroll: true });
    if (node.getBoundingClientRect().top < 0) window.scrollTo(0, 0);
  };
  return (
    <a
      href={`#${target}`}
      onClick={skip}
      data-testid="skip-link"
      className="sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-3 focus:z-skip focus:rounded-md focus:bg-white focus:px-4 focus:py-3 focus:text-label-lg focus:font-semibold focus:text-touki-black focus:shadow-tk-card"
    >
      {l.label}
    </a>
  );
}
